import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { ChatSession } from '../models/chat-session.model';
import { ChatMessage } from '../models/chat-message.model';
import { SessionCanvasRouteRecord } from '../models/session-canvas-route-record.model';
import { PickerRecentRecord } from '../shared/components/entity-dropdown-picker/picker-recent.model';

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {

  private readonly dbName = 'ChatAppDB';
  private dbVersion = 3; // Increment this if you change the schema
  private db: IDBDatabase | null = null;
  private dbReadySubject = new BehaviorSubject<boolean>(false);
  public dbReady$: Observable<boolean> = this.dbReadySubject.asObservable().pipe(
    filter(ready => ready), // Only emit true when ready
    take(1) // Complete after emitting true once, so subscribers don't hang indefinitely if DB fails
  );

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.initDb();
    } else {
      // If not in a browser, IndexedDB is not available.
      // Complete the subject so dependent services don't hang.
      this.dbReadySubject.next(false); // Indicate DB is not ready/available
      this.dbReadySubject.complete();
    }
  }

  private async initDb(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || typeof indexedDB === 'undefined') { // Double check for safety
      this.dbReadySubject.next(false);
      this.dbReadySubject.complete(); // Complete subject if DB is not supported
      return;
    }

    try {
      this.db = await new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(this.dbName, this.dbVersion);

        request.onerror = (event) => {
          reject((event.target as IDBRequest).error);
        };

        request.onsuccess = (event) => {
          resolve((event.target as IDBOpenDBRequest).result);
        };

        request.onupgradeneeded = (event) => {
          const dbInstance = (event.target as IDBOpenDBRequest).result;
          const transaction = (event.target as IDBOpenDBRequest).transaction;

          if (!dbInstance.objectStoreNames.contains('sessions')) {
            dbInstance.createObjectStore('sessions', { keyPath: 'sessionId' });
          }

          if (!dbInstance.objectStoreNames.contains('pickerRecents')) {
            const recentStore = dbInstance.createObjectStore('pickerRecents', { keyPath: 'key' });
            recentStore.createIndex('entityKey', 'entityKey', { unique: false });
            recentStore.createIndex('userKey', 'userKey', { unique: false });
            recentStore.createIndex('lastUsedAt', 'lastUsedAt', { unique: false });
          }

          if (!dbInstance.objectStoreNames.contains('sessionCanvasRoutes')) {
            const routeStore = dbInstance.createObjectStore('sessionCanvasRoutes', { keyPath: 'key' });
            routeStore.createIndex('userId', 'userId', { unique: false });
            routeStore.createIndex('sessionId', 'sessionId', { unique: false });
            routeStore.createIndex('updatedAt', 'updatedAt', { unique: false });
          }

          // Ensure chatMessages store uses messageId as keyPath
          if (!dbInstance.objectStoreNames.contains('chatMessages')) {
            const messageStore = dbInstance.createObjectStore('chatMessages', { keyPath: 'messageId' });
            messageStore.createIndex('sessionId', 'sessionId', { unique: false });
            messageStore.createIndex('timestamp', 'timestamp', { unique: false });
          } else {
            // If store exists, check its keyPath. This is more robust for migrations.
            // For this to trigger an actual change on an existing DB, dbVersion must be incremented.
            const oldMessageStore = transaction!.objectStore('chatMessages');
            if (oldMessageStore.keyPath !== 'messageId') {
              dbInstance.deleteObjectStore('chatMessages');
              const messageStore = dbInstance.createObjectStore('chatMessages', { keyPath: 'messageId' });
              messageStore.createIndex('sessionId', 'sessionId', { unique: false });
              messageStore.createIndex('timestamp', 'timestamp', { unique: false });
            }
          }
        };
      });
      this.dbReadySubject.next(true);
    } catch (error) {
      this.dbReadySubject.next(false);
      this.dbReadySubject.complete();
    }
  }

  private getObjectStore(storeName: string, mode: IDBTransactionMode): IDBObjectStore | null {
    if (!this.db) {
      return null;
    }
    try {
      const tx = this.db.transaction(storeName, mode);
      return tx.objectStore(storeName);
    } catch (e) {
      return null;
    }
  }

  private async waitForDbReady(): Promise<boolean> {
    try {
      await firstValueFrom(this.dbReady$);
      return true;
    } catch {
      return false;
    }
  }

  private getSessionCanvasRouteKey(userId: number, sessionId: number): string {
    return `${userId}:${sessionId}`;
  }

  public async cachePickerRecent<T>(record: PickerRecentRecord<T>): Promise<void> {
    const ready = await this.waitForDbReady();
    if (!ready) {
      return;
    }
    const store = this.getObjectStore('pickerRecents', 'readwrite');
    if (!store) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const request = store.put(record);
      request.onsuccess = () => resolve();
      request.onerror = (event) => reject((event.target as IDBRequest).error);
    });
  }

  public async getPickerRecentRecords<T>(entityKey: string, userKey: string): Promise<PickerRecentRecord<T>[]> {
    const ready = await this.waitForDbReady();
    if (!ready) {
      return [];
    }
    const store = this.getObjectStore('pickerRecents', 'readonly');
    if (!store) {
      return [];
    }

    return await new Promise<PickerRecentRecord<T>[]>((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = (event) => {
        const records = ((event.target as IDBRequest<PickerRecentRecord<T>[]>).result || [])
          .filter((record) => record.entityKey === entityKey && record.userKey === userKey)
          .sort((a, b) => b.lastUsedAt - a.lastUsedAt);
        resolve(records);
      };
      request.onerror = (event) => reject((event.target as IDBRequest).error);
    });
  }

  public async clearPickerRecentRecords(entityKey?: string, userKey?: string): Promise<void> {
    const ready = await this.waitForDbReady();
    if (!ready) {
      return;
    }
    const store = this.getObjectStore('pickerRecents', 'readwrite');
    if (!store) {
      return;
    }

    if (!entityKey && !userKey) {
      await new Promise<void>((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = (event) => reject((event.target as IDBRequest).error);
      });
      return;
    }

    const records = await this.getPickerRecentRecords(entityKey ?? '', userKey ?? '');
    await Promise.all(
      records.map(
        (record) =>
          new Promise<void>((resolve, reject) => {
            const request = store.delete(record.key);
            request.onsuccess = () => resolve();
            request.onerror = (event) => reject((event.target as IDBRequest).error);
          })
      )
    );
  }

  public async getSessionCanvasRoutesForUser(userId: number): Promise<SessionCanvasRouteRecord[]> {
    const ready = await this.waitForDbReady();
    if (!ready) {
      return [];
    }

    const store = this.getObjectStore('sessionCanvasRoutes', 'readonly');
    if (!store) {
      return [];
    }

    const index = store.index('userId');
    return new Promise<SessionCanvasRouteRecord[]>((resolve, reject) => {
      const request = index.getAll(userId);
      request.onsuccess = (event) => {
        const records = (event.target as IDBRequest<SessionCanvasRouteRecord[]>).result || [];
        resolve(records.sort((a, b) => b.updatedAt - a.updatedAt));
      };
      request.onerror = (event) => reject((event.target as IDBRequest).error);
    });
  }

  public async getSessionCanvasRoute(userId: number, sessionId: number): Promise<SessionCanvasRouteRecord | null> {
    const ready = await this.waitForDbReady();
    if (!ready) {
      return null;
    }

    const store = this.getObjectStore('sessionCanvasRoutes', 'readonly');
    if (!store) {
      return null;
    }

    return new Promise<SessionCanvasRouteRecord | null>((resolve, reject) => {
      const request = store.get(this.getSessionCanvasRouteKey(userId, sessionId));
      request.onsuccess = (event) => resolve((event.target as IDBRequest<SessionCanvasRouteRecord>).result || null);
      request.onerror = (event) => reject((event.target as IDBRequest).error);
    });
  }

  public async setSessionCanvasRoute(userId: number, sessionId: number, path: string): Promise<void> {
    const ready = await this.waitForDbReady();
    if (!ready) {
      return;
    }

    const store = this.getObjectStore('sessionCanvasRoutes', 'readwrite');
    if (!store) {
      return;
    }

    const record: SessionCanvasRouteRecord = {
      key: this.getSessionCanvasRouteKey(userId, sessionId),
      userId,
      sessionId,
      path,
      updatedAt: Date.now(),
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.put(record);
      request.onsuccess = () => resolve();
      request.onerror = (event) => reject((event.target as IDBRequest).error);
    });
  }

  public async removeSessionCanvasRoute(userId: number, sessionId: number): Promise<void> {
    const ready = await this.waitForDbReady();
    if (!ready) {
      return;
    }

    const store = this.getObjectStore('sessionCanvasRoutes', 'readwrite');
    if (!store) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(this.getSessionCanvasRouteKey(userId, sessionId));
      request.onsuccess = () => resolve();
      request.onerror = (event) => reject((event.target as IDBRequest).error);
    });
  }

  public cacheSession(session: ChatSession): Promise<void> {
    return new Promise(async (resolve, reject) => {
      await this.dbReady$;
      const store = this.getObjectStore('sessions', 'readwrite');
      if (!store) return reject(new Error("Store 'sessions' not available"));
      const { chatMessages, ...sessionMeta } = session; // Store session metadata without messages
      const request = store.put(sessionMeta);
      request.onsuccess = () => resolve();
      request.onerror = (event) => reject((event.target as IDBRequest).error);
    });
  }

  public getCachedSessions(): Promise<ChatSession[]> {
    return new Promise(async (resolve, reject) => {
      await this.dbReady$;
      const store = this.getObjectStore('sessions', 'readonly');
      if (!store) return reject(new Error("Store 'sessions' not available"));
      const request = store.getAll();
      request.onsuccess = (event) => resolve((event.target as IDBRequest<ChatSession[]>).result || []);
      request.onerror = (event) => reject((event.target as IDBRequest).error);
    });
  }

  public clearCachedSessions(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      await this.dbReady$;
      const store = this.getObjectStore('sessions', 'readwrite');
      if (!store) return reject(new Error("Store 'sessions' not available"));
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = (event) => reject((event.target as IDBRequest).error);
    });
  }

  public getLatestSessionUpdateTimestamp(): Promise<number | null> {
    return new Promise(async (resolve, reject) => {
      await this.dbReady$;
      // Efficiently get the latest message timestamp from the 'chatMessages' store
      const messageStore = this.getObjectStore('chatMessages', 'readonly');
      if (!messageStore) {
        return resolve(null); // Or reject, depending on desired error handling
      }

      const timestampIndex = messageStore.index('timestamp');
      // Open a cursor on the timestamp index, in reverse order to get the latest first
      const request = timestampIndex.openCursor(null, 'prev'); // 'prev' for descending order

      request.onsuccess = async (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor && cursor.value) {
          const latestMessage = cursor.value as ChatMessage;
          const latestTimestamp = new Date(latestMessage.timestamp).getTime();
          resolve(!isNaN(latestTimestamp) && latestTimestamp > 0 ? latestTimestamp : null);
        } else {
          // No messages found in the store
          resolve(null);
        }
      };
      request.onerror = (event) => reject((event.target as IDBRequest).error);
    });
  }
  
  public removeCachedSession(sessionId: number): Promise<void> {
    return new Promise(async (resolve, reject) => {
      await this.dbReady$;
      const store = this.getObjectStore('sessions', 'readwrite');
      if (!store) return reject(new Error("Store 'sessions' not available"));
      const request = store.delete(sessionId);
      request.onsuccess = () => resolve();
      request.onerror = (event) => reject((event.target as IDBRequest).error);
    });
  }

  public cacheChatMessage(message: ChatMessage): Promise<void> {
    return new Promise(async (resolve, reject) => {
      if (message.messageId === undefined || message.messageId === null) {
        return resolve(); // Resolve immediately as there's nothing to cache
      }

      await this.dbReady$;
      if (!this.db) return reject(new Error("IndexedDB is not available."));

      const tx = this.db.transaction('chatMessages', 'readwrite');
      const store = tx.objectStore('chatMessages');

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);

      const getRequest = store.getKey(message.messageId!);
      getRequest.onsuccess = () => {
        if (getRequest.result === undefined) {
          // Key does not exist, so add it.
          store.add(message);
        }
      };
    });
  }

  public cacheChatMessages(messages: ChatMessage[]): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const validMessages = messages.filter(m => m.messageId !== undefined && m.messageId !== null);
      const invalidMessages = messages.filter(m => m.messageId === undefined || m.messageId === null);

      if (invalidMessages.length > 0) {
      }
      if (validMessages.length === 0) {
        return resolve(); // Resolve if no valid messages to process
      }

      await this.dbReady$;
      if (!this.db) return reject(new Error("IndexedDB is not available."));

      const tx = this.db.transaction('chatMessages', 'readwrite');
      const store = tx.objectStore('chatMessages');

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);

      validMessages.forEach(message => {
        const getRequest = store.getKey(message.messageId!);
        getRequest.onsuccess = () => {
          if (getRequest.result === undefined) {
            // Key does not exist, so we add it.
            store.add(message);
          }
        };
      });
    });
  }

  public async getLatestMessageTimestampForSession(sessionId: number): Promise<number | null> {
    return new Promise(async (resolve, reject) => {
      try {
        const messages = await this.getCachedMessagesForSession(sessionId); // This already logs

        if (messages.length === 0) {
          resolve(null);
        } else {
          // getCachedMessagesForSession sorts by timestamp, so the last one is the latest
          const lastMessage = messages[messages.length - 1];
          const rawTimestamp = lastMessage.timestamp;

          const dateObject = new Date(rawTimestamp);

          const latestTimestamp = dateObject.getTime();

          if (isNaN(latestTimestamp)) {
            resolve(null);
          } else if (latestTimestamp > 0) {
            resolve(latestTimestamp);
          } else {
            resolve(null);
          }
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  public getCachedMessagesForSession(sessionId: number): Promise<ChatMessage[]> {
    return new Promise(async (resolve, reject) => {
      await this.dbReady$;
      if (!this.db) {
        return reject(new Error("IndexedDbService: DB not initialized when trying to get messages."));
      }
      const store = this.getObjectStore('chatMessages', 'readonly');
      if (!store) {
        return reject(new Error("Store 'chatMessages' not available"));
      }

      const index = store.index('sessionId');
      const request = index.getAll(sessionId);

      request.onsuccess = (event) => {
        const messages = (event.target as IDBRequest<ChatMessage[]>).result || [];
        resolve(messages.sort((a,b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()));
      };
      request.onerror = (event) => {
        const error = (event.target as IDBRequest).error;
        reject(error);
      };

      store.transaction.oncomplete = () => {
      };
      store.transaction.onabort = (event) => {
        const error = (event.target as IDBTransaction).error;
        // If the transaction aborts, the original promise might have already resolved or rejected.
        // This log helps identify if an abort is happening silently.
      };
    });
  }

  public removeCachedMessagesForSession(sessionId: number): Promise<void> {
    return new Promise(async (resolve, reject) => {
      await this.dbReady$;
      const store = this.getObjectStore('chatMessages', 'readwrite');
      if (!store) return reject(new Error("Store 'chatMessages' not available"));
      const index = store.index('sessionId');
      const request = index.openCursor(IDBKeyRange.only(sessionId));
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = (event) => reject((event.target as IDBRequest).error);
    });
  }

  public async getCachedSessionWithMessages(sessionId: number): Promise<ChatSession | null> {
    await this.dbReady$;
    const store = this.getObjectStore('sessions', 'readonly');
    if (!store) {
      return null; // Or reject(new Error("Store 'sessions' not available"));
    }
  
    const numericSessionId = +sessionId; // Ensure sessionId is a number

  
    return new Promise(async (resolve, reject) => {
        const request = store.get(numericSessionId); // Use the numeric ID
  
        request.onsuccess = async (event) => {
            const result = (event.target as IDBRequest<ChatSession>).result;
  
            let session: ChatSession | null = result; // Assign directly
  
            if (session) {
                session.chatMessages = await this.getCachedMessagesForSession(numericSessionId); // Use numeric ID
                resolve(session);
            } else {
                resolve(null);
            }
        };
        request.onerror = (event) => {
            const error = (event.target as IDBRequest).error;
            reject(error);
        };
    });
  }
}
